import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const AuditLogViewer = ({ logs }) => {
  const [selectedAction, setSelectedAction] = useState('all');
  const [selectedSeverity, setSelectedSeverity] = useState('all');

  const actionOptions = [
    { value: 'all', label: 'All Actions' },
    { value: 'login', label: 'Login' },
    { value: 'logout', label: 'Logout' },
    { value: 'create', label: 'Create' },
    { value: 'update', label: 'Update' },
    { value: 'delete', label: 'Delete' },
    { value: 'export', label: 'Export' }
  ];

  const severityOptions = [
    { value: 'all', label: 'All Severity' },
    { value: 'info', label: 'Info' },
    { value: 'warning', label: 'Warning' },
    { value: 'critical', label: 'Critical' }
  ];

  const filteredLogs = logs?.filter(log => {
    const matchesAction = selectedAction === 'all' || log?.action === selectedAction;
    const matchesSeverity = selectedSeverity === 'all' || log?.severity === selectedSeverity;
    return matchesAction && matchesSeverity;
  });

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'text-error';
      case 'warning': return 'text-warning';
      case 'info': return 'text-primary';
      default: return 'text-muted-foreground';
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'login': return 'LogIn';
      case 'logout': return 'LogOut';
      case 'create': return 'Plus';
      case 'update': return 'Edit';
      case 'delete': return 'Trash2';
      case 'export': return 'Download';
      default: return 'Activity';
    }
  };

  return (
    <div className="bg-card rounded-xl shadow-elevation-2 border border-border">
      <div className="p-4 md:p-6 border-b border-border">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h2 className="text-lg md:text-xl font-semibold text-foreground mb-1">Audit Log</h2>
            <p className="text-sm text-muted-foreground">System activity and security events</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Select
              options={actionOptions}
              value={selectedAction}
              onChange={setSelectedAction}
              placeholder="Filter by action"
              className="w-full sm:w-40"
            />
            <Select
              options={severityOptions}
              value={selectedSeverity}
              onChange={setSelectedSeverity}
              placeholder="Filter by severity"
              className="w-full sm:w-40"
            />
            <Button variant="outline" size="sm" iconName="Download" iconPosition="left">
              Export
            </Button>
          </div>
        </div>
      </div>
      <div className="max-h-96 md:max-h-[500px] overflow-y-auto">
        <div className="divide-y divide-border">
          {filteredLogs?.map((log) => (
            <div key={log?.id} className="p-4 md:p-5 hover:bg-muted/30 transition-smooth">
              <div className="flex items-start gap-3 md:gap-4">
                <div className={`w-8 h-8 md:w-10 md:h-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0 ${getSeverityColor(log?.severity)}`}>
                  <Icon name={getActionIcon(log?.action)} size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm md:text-base font-medium text-foreground mb-1">{log?.description}</h3>
                      <p className="text-xs md:text-sm text-muted-foreground">by {log?.user}</p>
                    </div>
                    <span className={`text-xs font-medium ${getSeverityColor(log?.severity)} whitespace-nowrap`}>
                      {log?.severity?.toUpperCase()}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 md:gap-4 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Icon name="Clock" size={12} />
                      <span>{log?.timestamp}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Icon name="MapPin" size={12} />
                      <span>{log?.ipAddress}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Icon name="Monitor" size={12} />
                      <span>{log?.device}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {filteredLogs?.length === 0 && (
        <div className="p-8 md:p-12 text-center">
          <Icon name="FileText" size={48} className="mx-auto mb-4 text-muted-foreground" />
          <p className="text-sm md:text-base text-muted-foreground">No audit logs found matching your filters</p>
        </div>
      )}
      <div className="p-4 md:p-6 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4">
        <p className="text-xs md:text-sm text-muted-foreground">
          Showing {filteredLogs?.length} of {logs?.length} logs
        </p>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" iconName="ChevronLeft">Previous</Button>
          <Button variant="outline" size="sm" iconName="ChevronRight">Next</Button>
        </div>
      </div>
    </div>
  );
};

export default AuditLogViewer;